import React from "react";
import { useLang } from "../i18n";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

export const FieldRenderer = ({ field, value, onChange, error }) => {
  const { lang, t } = useLang();
  const [comboOpen, setComboOpen] = React.useState(false);
  const label = lang === "fr" ? field.fr : field.en;
  const help = lang === "fr" ? field.helpFr : field.helpEn;
  const testId = `field-${field.name}`;

  const labelEl = (
    <Label htmlFor={testId} className="text-sm font-semibold text-slate-700">
      {label} {field.required && <span className="text-primary">*</span>}
    </Label>
  );

  if (field.type === "textarea") {
    return (
      <div className="space-y-1.5">
        {labelEl}
        {help && <p className="text-xs text-slate-500">{help}</p>}
        <Textarea
          id={testId}
          data-testid={testId}
          rows={field.rows || 3}
          value={value || ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          className="resize-y bg-white"
        />
        {error && <p className="text-xs text-destructive font-medium">{error}</p>}
      </div>
    );
  }

  if (field.type === "combobox") {
    const selected = field.options.find((o) => o.value === value);
    return (
      <div className="space-y-1.5">
        {labelEl}
        <Popover open={comboOpen} onOpenChange={setComboOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              id={testId}
              data-testid={testId}
              className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <span className={selected ? "truncate" : "text-muted-foreground truncate"}>
                {selected ? (lang === "fr" ? selected.fr : selected.en) : (lang === "fr" ? "Sélectionner…" : "Select…")}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
            <Command>
              <CommandInput placeholder={lang === "fr" ? "Rechercher une faculté…" : "Search…"} data-testid={`${testId}-search`} />
              <CommandList>
                <CommandEmpty>{lang === "fr" ? "Aucun résultat." : "No result."}</CommandEmpty>
                <CommandGroup>
                  {field.options.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={lang === "fr" ? opt.fr : opt.en}
                      data-testid={`${testId}-${opt.value}`}
                      onSelect={() => { onChange(field.name, opt.value); setComboOpen(false); }}
                    >
                      <Check className={`mr-2 h-4 w-4 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                      {lang === "fr" ? opt.fr : opt.en}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-destructive font-medium">{error}</p>}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="space-y-1.5">
        {labelEl}
        {help && <p className="text-xs text-slate-500">{help}</p>}
        <Select value={value || ""} onValueChange={(v) => onChange(field.name, v)}>
          <SelectTrigger id={testId} data-testid={testId} className="bg-white">
            <SelectValue placeholder={lang === "fr" ? "Sélectionner…" : "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`${testId}-${opt.value}`}>
                {lang === "fr" ? opt.fr : opt.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-destructive font-medium">{error}</p>}
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div className="space-y-2">
        {labelEl}
        <div className="flex flex-wrap gap-2" data-testid={testId}>
          {field.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                data-testid={`${testId}-${opt.value}`}
                onClick={() => onChange(field.name, opt.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  selected
                    ? "bg-primary text-white border-primary shadow-sm"
                    : "bg-white text-slate-600 border-slate-300 hover:border-primary/50 hover:bg-slate-50"
                }`}
              >
                {lang === "fr" ? opt.fr : opt.en}
              </button>
            );
          })}
        </div>
        {error && <p className="text-xs text-destructive font-medium">{error}</p>}
      </div>
    );
  }

  if (field.type === "yesno") {
    return (
      <div className="flex items-center justify-between gap-4 py-2 border-b border-slate-100">
        <span className="text-sm font-medium text-slate-700">
          {label} {field.required && <span className="text-primary">*</span>}
        </span>
        <div className="flex gap-1.5 flex-shrink-0" data-testid={testId}>
          {[
            { v: true, txt: t("yes") },
            { v: false, txt: t("no") },
          ].map((o) => {
            const selected = value === o.v;
            return (
              <button
                type="button"
                key={String(o.v)}
                data-testid={`${testId}-${o.v ? "yes" : "no"}`}
                onClick={() => onChange(field.name, o.v)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  selected
                    ? o.v
                      ? "bg-primary text-white border-primary"
                      : "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {o.txt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // text, tel, email, number
  return (
    <div className="space-y-1.5">
      {labelEl}
      <div className="relative">
        {field.prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            {field.prefix}
          </span>
        )}
        <Input
          id={testId}
          data-testid={testId}
          type={field.type === "number" ? "number" : field.type === "email" ? "email" : field.type === "tel" ? "tel" : "text"}
          inputMode={field.type === "number" ? "decimal" : undefined}
          min={field.type === "number" ? 0 : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(field.name, e.target.value)}
          className={`bg-white ${field.prefix ? "pl-7" : ""}`}
        />
      </div>
      {error && <p className="text-xs text-destructive font-medium">{error}</p>}
    </div>
  );
};
